import React, { useState } from 'react';
import { 
  Upload, 
  FileText, 
  Check, 
  X, 
  Sparkles, 
  ShieldCheck, 
  AlertCircle,
  RefreshCw,
  UserCheck
} from 'lucide-react';
import { MasterProfile } from '../types';
import { parseCvTextToProfile } from '../services/cvParserService';
import { storageService } from '../services/storageService';

interface UploadCvModalProps {
  isOpen: boolean;
  onClose: () => void;
  masterProfile: MasterProfile;
  onProfileUpdated: (p: MasterProfile) => void;
}

export const UploadCvModal: React.FC<UploadCvModalProps> = ({
  isOpen,
  onClose,
  masterProfile,
  onProfileUpdated
}) => {
  const [cvText, setCvText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [extractedSkills, setExtractedSkills] = useState<string[]>([]);

  if (!isOpen) return null;

  // Handle File Upload (.txt or drag-drop text file)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        setCvText(content);
      }
    };

    reader.readAsText(file);
  };

  const handleParseAndUpdate = () => {
    if (!cvText.trim()) return;
    setIsProcessing(true);

    const updatedProfile = parseCvTextToProfile(cvText, masterProfile);
    setExtractedSkills(updatedProfile.skills);

    // Save to storage & Cloudflare D1
    storageService.saveProfile(updatedProfile);
    onProfileUpdated(updatedProfile);

    setIsProcessing(false);
    setIsSuccess(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto animate-in fade-in">
      <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl my-8">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Upload Your CV (Cybersecurity & MSP)</h3>
              <p className="text-xs text-slate-400">Paste or upload your CV text so the AI learns your exact strengths</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isSuccess ? (
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-6 text-center space-y-4 animate-in fade-in">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto">
              <Check className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-base font-bold text-white">CV Analyzed & Profile Updated!</h4>
              <p className="text-xs text-slate-300 mt-1 max-w-md mx-auto">
                The system now understands your exact Cybersecurity, InfoSec, and MSP background.
              </p>
            </div>

            {/* Extracted Skills Badges */}
            <div className="space-y-1.5 text-left bg-slate-950 p-4 rounded-xl border border-slate-800">
              <span className="text-[11px] font-bold text-emerald-400 uppercase tracking-wider">
                Recognized Core Strengths ({extractedSkills.length}):
              </span>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {extractedSkills.map((sk, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-md text-[11px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-medium">
                    ✓ {sk}
                  </span>
                ))}
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-full py-3 px-4 rounded-xl text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/25 transition-all"
            >
              Start Tailoring Jobs with My Updated Profile ➔
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            
            {/* File Upload Drop Zone */}
            <div className="border-2 border-dashed border-slate-800 hover:border-emerald-500/50 rounded-2xl p-6 text-center space-y-2 bg-slate-950/60 transition-all">
              <Upload className="w-8 h-8 text-emerald-400 mx-auto" />
              <div className="text-xs text-slate-300">
                <label className="font-bold text-emerald-400 hover:underline cursor-pointer">
                  <span>Upload a file (.txt, .md)</span>
                  <input
                    type="file"
                    accept=".txt,.md,.text"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                <span className="text-slate-500"> or paste your full CV text below</span>
              </div>
              {fileName && (
                <div className="text-xs text-emerald-300 font-semibold flex items-center justify-center space-x-1">
                  <FileText className="w-3.5 h-3.5" />
                  <span>Loaded: {fileName}</span>
                </div>
              )}
            </div>

            {/* Raw Text Input */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-300">
                Paste Your Full CV / Resume Text:
              </label>
              <textarea
                rows={7}
                value={cvText}
                onChange={(e) => setCvText(e.target.value)}
                placeholder="Paste your CV text here (Summary, Work Experience, Security Certifications, SIEM tools, MSP history, etc.)..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-slate-200 focus:border-emerald-500 outline-none font-mono leading-relaxed"
              />
            </div>

            {/* Quick Cyber Sample Loader */}
            <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1">
              <span>Need a fast template?</span>
              <button
                type="button"
                onClick={() => {
                  setCvText(`Senior Cybersecurity & Information Security Specialist
Location: London, UK (Remote)
Phone: +44 7700 900123 | Email: consultant@infosec-uk.example.com

PROFESSIONAL SUMMARY:
Cybersecurity and Information Security consultant with 6+ years of hands-on experience supporting UK Managed Service Providers (MSPs), enterprises, and financial clients. Specialist in Cyber Essentials Plus readiness, vulnerability management, SIEM monitoring (Splunk, Microsoft Sentinel), incident response, and Microsoft 365 / Entra ID security hardening.

CORE COMPETENCIES & TECHNICAL SKILLS:
- Cybersecurity & Information Security Architecture
- MSP IT & Security Operations (Tier 2/3 Escalations)
- Cyber Essentials & Cyber Essentials Plus Audits
- ISO 27001 & NIST Cybersecurity Framework
- SIEM Log Analysis & Threat Detection (Sentinel, Splunk)
- Vulnerability Scanning & Remediation (Qualys, Nessus)
- Endpoint Security & EDR (Microsoft Defender, CrowdStrike)
- Firewall & Network Security Configuration (Fortinet, Cisco, pfSense)
- Microsoft 365, Entra ID / Azure AD Security Hardening & MFA
- Incident Response Triage & Phishing Simulation Campaigns

PROFESSIONAL EXPERIENCE:
* Lead InfoSec & MSP Security Consultant - Apex MSP Cloud Solutions (2022 - Present):
  - Directed cybersecurity audits and Cyber Essentials Plus certifications for 35+ UK SME clients.
  - Implemented automated Microsoft Sentinel alerting and EDR deployment, reducing incident containment times by 60%.
  - Provided emergency security incident triage and ransomware prevention protocols for MSP client networks.

* Information Security Analyst - Vanguard Cyber Defense (2019 - 2022):
  - Conducted weekly vulnerability assessments and prioritized patching for 200+ servers and cloud endpoints.
  - Configured firewall policies, zero-trust network access, and MFA enforcement across hybrid environments.`);
                }}
                className="text-emerald-400 hover:underline font-semibold"
              >
                + Load Senior Cyber & MSP Template
              </button>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleParseAndUpdate}
              disabled={isProcessing || !cvText.trim()}
              className="w-full py-3.5 px-6 rounded-xl font-bold text-sm bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/25 flex items-center justify-center space-x-2 transition-all disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Extracting Skills & Updating Profile...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Save & Update My Profile Now</span>
                </>
              )}
            </button>

          </div>
        )}

      </div>
    </div>
  );
};
